// @ts-strict-ignore
import { call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { connect, disconnect } from '@giantmachines/redux-websocket';
import { logoutSuccess, LOGOUT_SUCCESS } from '../actions/login';
import { newNotification } from '../actions/notifications';
import {
    selectAuthCredentials,
    selectCanAttemptReconnect,
    selectIsAttemptingWsReconnect,
    selectOrg,
    selectWsIsConnected,
} from '../selectors/selectors';
import { Environment } from '../types';
import { wsMessageSaga } from './sagas';
import * as API from '../api';
import qs from 'query-string';
import {
    TryWebsocketReconnectAction,
    TRY_WEBSOCKET_RECONNECT,
    TRY_WEBSOCKET_DISCONNECT,
    updateWsAttemptingReconnect,
    updateWsConnected,
    TRY_WEBSOCKET_CONNECT,
    tryWebsocketReconnect,
} from '../actions/ws';
import { AuthResponse } from '../apiResponseTypes';
import { authSuccess, clearAuth } from '../actions/auth';
import * as Logger from '../utils/logger';
import { isDevelopmentEnvironment } from '../helpers';

export const REDUX_WEBSOCKET_MESSAGE = 'REDUX_WEBSOCKET::MESSAGE';
export const REDUX_WEBSOCKET_OPEN = 'REDUX_WEBSOCKET::OPEN';
export const REDUX_WEBSOCKET_CLOSED = 'REDUX_WEBSOCKET::CLOSED';
export const REDUX_WEBSOCKET_ERROR = 'REDUX_WEBSOCKET::ERROR';
export const REDUX_WEBSOCKET_CONNECT = 'REDUX_WEBSOCKET::CONNECT';

export const WS_RETRY_DELAY = 10000;

export function* generateAuthdWebsocketUrl() {
    Logger.log(`generating ws url`);

    const org = yield select(selectOrg);

    if (!org) {
        throw Error('No org found');
    }

    const authCredentials = yield select(selectAuthCredentials);
    const authToken = yield call(API.getShortLivedWebsocketConnectionToken, authCredentials);
    const queryParams = {
        authToken,
    };
    const encodedParams = qs.stringify(queryParams);

    const isDevEnvironment = isDevelopmentEnvironment();

    const urlPrefix = isDevEnvironment ? 'ws://' : 'wss://';

    return `${urlPrefix}${document.location.host}/org/${org.id}/ws?${encodedParams}`;
}

function* closeWebsocket() {
    const isWsConnected = yield select(selectWsIsConnected);

    if (isWsConnected) {
        yield put(disconnect());
    }
}

export function* startWsConnectionSaga() {
    Logger.log('start ws connection saga called... ');
    try {
        const url = yield call(generateAuthdWebsocketUrl);
        Logger.log(`auth'd ws url generated, attempting to start ws connection...`);

        yield put(connect(url));
    } catch {
        Logger.log('unable to start websocket connection');
    }
}

export function* attemptWsConnectWithReconnect(action: TryWebsocketReconnectAction) {
    const canAttemptReconnect = yield select(selectCanAttemptReconnect);

    if (!canAttemptReconnect) {
        Logger.log('cannot attempt reconnect, returning...');
        return;
    }

    const date = new Date();

    Logger.log(`beginning attempt reconnect... [at ${date}]`);
    yield put(updateWsAttemptingReconnect({ isAttemptingReconnect: true }));

    try {
        const url = yield call(generateAuthdWebsocketUrl);
        Logger.log(`auth'd ws url generated, attempting connect...`);

        yield put(connect(url));
    } catch (e) {
        Logger.log(`connect ws failed with error ${e}`);

        // failed to generate temp token, can't start ws connection
        if (e.name === API.RunwayErrorType.Unauthorized) {
            // attempt to fetch new token
            try {
                Logger.log('attempting to refresh token...');
                const response: AuthResponse = yield call(API.refreshToken);

                Logger.log('refresh token success');

                yield put(authSuccess(response));
            } catch (e) {
                Logger.log(`refresh auth token failed: ${e}`);

                if (e.name === API.RunwayErrorType.Unauthorized) {
                    Logger.log('token expired, logging out');
                    // refresh token failed, log out
                    yield put(clearAuth());
                    yield put(logoutSuccess());

                    yield put(updateWsAttemptingReconnect({ isAttemptingReconnect: false }));

                    return;
                }
            }
        }

        Logger.log('delaying, and then retrying...');
        yield delay(action.payload.retryInterval);

        yield put(tryWebsocketReconnect(action.payload));
    }
}

function* wsOpenSaga() {
    if (process.env.NODE_ENV !== Environment.Production) {
        const notif = {
            title: 'Websocket opened!',
            message: '',
        };

        yield put(newNotification(notif));
    }
    const date = new Date();

    Logger.log(`ws opened! at ${date}`);

    yield put(updateWsConnected({ isConnected: true }));
    yield put(updateWsAttemptingReconnect({ isAttemptingReconnect: false }));
}

export function* wsClosedSaga() {
    if (process.env.NODE_ENV !== Environment.Production) {
        const notif = {
            title: 'Websocket closed',
            message: '',
        };

        yield put(newNotification(notif));
    }

    const date = new Date();

    Logger.log(`ws closed! at ${date}`);

    yield put(updateWsConnected({ isConnected: false }));

    /**
     * in order to attempt a reconnect, following conditions must be met:
     * - user must not already be connected
     * - connection must have opened at least once
     * - user must be logged in
     * - state must be fetched (since orgId is required to connect)
     * - must not be already attempting a reconnect
     * - must not have explicitly disabled ws connections
     *  */

    const canAttemptReconnect = yield select(selectCanAttemptReconnect);
    const isAttemptingReconnect = yield select(selectIsAttemptingWsReconnect);

    if (canAttemptReconnect && !isAttemptingReconnect) {
        Logger.log(`met reconnect conditions, will attempt reconnect...`);

        yield put(tryWebsocketReconnect({ retryInterval: WS_RETRY_DELAY }));
    } else {
        Logger.log(`did not meet conditions for reconnect, will not attempt`);
    }
}

// eslint-disable-next-line require-yield
function* wsMessageErrorSaga(action) {
    Logger.log('ws error!');
    Logger.log(action);
}

/* Watch */

export function* watchWebsocketReconnect() {
    yield takeLatest(TRY_WEBSOCKET_RECONNECT, attemptWsConnectWithReconnect);
}

export function* watchWebsocketConnect() {
    yield takeLatest(TRY_WEBSOCKET_CONNECT, startWsConnectionSaga);
}

export function* watchWebsocketOpen() {
    yield takeEvery(REDUX_WEBSOCKET_OPEN, wsOpenSaga);
}

export function* watchWebsocketClosed() {
    yield takeEvery(REDUX_WEBSOCKET_CLOSED, wsClosedSaga);
}

export function* watchWebsocketMessage() {
    yield takeEvery(REDUX_WEBSOCKET_MESSAGE, wsMessageSaga);
}

export function* watchWebsocketError() {
    yield takeEvery(REDUX_WEBSOCKET_ERROR, wsMessageErrorSaga);
}

export function* watchLogoutSuccess() {
    yield takeEvery([LOGOUT_SUCCESS, TRY_WEBSOCKET_DISCONNECT], closeWebsocket);
}
